import React, { createContext, useContext, useReducer, useEffect, ReactNode, useCallback, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useSelectedRestaurant } from './SelectedRestaurantContext';
import { MenuItem, Category, Order, OrderItem, WaiterRequest } from '../types';
import { adminMenuService } from '../services/adminMenu';
import { api } from '../services/api';
import { storageService } from '../services/storage';

interface AppState {
  categories: Category[];
  menuItems: MenuItem[];
  currentTable: number | null;
  currentOrder: Order | null;
  cart: OrderItem[];
  orders: Order[];
  waiterRequests: WaiterRequest[];
  loading: boolean;
  error: string | null;
  showDetailModal: boolean;
  hideTabBar: boolean; // Nuevo estado para ocultar TabBar
  hideTabBarOnScroll: boolean; // Estado para ocultar TabBar por scroll
  showCartModal: boolean; // Estado para controlar la visibilidad del modal del carrito
}

interface AuthState {
  user: any;
  isAuthenticated: boolean;
  isLoading: boolean;
}

type AppAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_CATEGORIES'; payload: Category[] }
  | { type: 'SET_MENU_ITEMS'; payload: MenuItem[] }
  | { type: 'SET_CURRENT_TABLE'; payload: number | null }
  | { type: 'SET_CURRENT_ORDER'; payload: Order | null }
  | { type: 'ADD_TO_CART'; payload: OrderItem }
  | { type: 'REMOVE_FROM_CART'; payload: string }
  | { type: 'UPDATE_CART_ITEM'; payload: { itemId: string; quantity: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'SET_CART'; payload: OrderItem[] }
  | { type: 'SET_ORDERS'; payload: Order[] }
  | { type: 'ADD_ORDER'; payload: Order }
  | { type: 'UPDATE_ORDER_STATUS'; payload: { orderId: string; status: Order['status'] } }
  | { type: 'SET_WAITER_REQUESTS'; payload: WaiterRequest[] }
  | { type: 'MARK_WAITER_REQUEST_ATTENDED'; payload: string }
  | { type: 'SET_SHOW_DETAIL_MODAL'; payload: boolean }
  | { type: 'SET_HIDE_TAB_BAR'; payload: boolean }
  | { type: 'SET_HIDE_TAB_BAR_ON_SCROLL'; payload: boolean }
  | { type: 'SET_SHOW_CART_MODAL'; payload: boolean }
  | { type: 'CLEAR_ALL_DATA'; payload: void };

const initialState: AppState = {
  categories: [],
  menuItems: [],
  currentTable: null,
  currentOrder: null,
  cart: [],
  orders: [],
  waiterRequests: [],
  loading: false,
  error: null,
  showDetailModal: false,
  hideTabBar: false,
  hideTabBarOnScroll: false,
  showCartModal: false,
};

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_CATEGORIES':
      return { ...state, categories: action.payload };
    case 'SET_MENU_ITEMS':
      return { ...state, menuItems: action.payload };
    case 'SET_CURRENT_TABLE':
      return { ...state, currentTable: action.payload };
    case 'SET_CURRENT_ORDER':
      return { ...state, currentOrder: action.payload };
    case 'ADD_TO_CART':
      return { ...state, cart: [...state.cart, action.payload] };
    case 'REMOVE_FROM_CART':
      return { ...state, cart: state.cart.filter(item => item.id !== action.payload) };
    case 'UPDATE_CART_ITEM':
      return {
        ...state,
        cart: state.cart.map(item =>
          item.id === action.payload.itemId
            ? { ...item, quantity: action.payload.quantity, price: item.menuItem.price * action.payload.quantity }
            : item
        ),
      };
    case 'CLEAR_CART':
      return { ...state, cart: [] };
    case 'SET_CART':
      return { ...state, cart: action.payload || [] };
    case 'SET_ORDERS':
      return { ...state, orders: action.payload };
    case 'ADD_ORDER':
      return { ...state, orders: [...state.orders, action.payload] };
    case 'UPDATE_ORDER_STATUS':
      return {
        ...state,
        orders: state.orders.map(order =>
          order.id === action.payload.orderId
            ? { ...order, status: action.payload.status }
            : order
        ),
      };
    case 'SET_WAITER_REQUESTS':
      return { ...state, waiterRequests: action.payload };
    case 'MARK_WAITER_REQUEST_ATTENDED':
      return {
        ...state,
        waiterRequests: state.waiterRequests.map(request =>
          request.id === action.payload
            ? { ...request, status: 'attended' }
            : request
        ),
      };
    case 'SET_SHOW_DETAIL_MODAL':
      return { ...state, showDetailModal: action.payload };
    case 'SET_HIDE_TAB_BAR':
      return { ...state, hideTabBar: action.payload };
    case 'SET_HIDE_TAB_BAR_ON_SCROLL':
      return { ...state, hideTabBarOnScroll: action.payload };
    case 'SET_SHOW_CART_MODAL':
      return { ...state, showCartModal: action.payload };
    case 'CLEAR_ALL_DATA':
      return initialState;
    default:
      return state;
  }
}

interface AppContextType extends AppState {
  auth: AuthState;
  dispatch: React.Dispatch<AppAction>;
  loadCategories: (restaurantId?: string) => Promise<void>;
  loadMenu: (restaurantId?: string) => Promise<void>;
  loadMenuByCategory: (categoryId: string) => Promise<void>;
  addToCart: (menuItem: MenuItem, quantity: number, specialInstructions?: string, sizeName?: string) => void;
  removeFromCart: (itemId: string) => void;
  updateCartItem: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  clearAllAppData: () => void;
  invalidateCache: (type: 'categories' | 'menu' | 'all') => void;
  placeOrder: (orderData: {
    tableSigned?: string;
    deliveryAddress?: string;
  }) => Promise<Order>;
  loadOrders: () => Promise<void>;
  updateOrderStatus: (orderId: string, status: Order['status']) => Promise<void>;
  requestWaiter: (requestType: WaiterRequest['requestType'], message?: string) => Promise<WaiterRequest>;
  loadWaiterRequests: () => Promise<void>;
  markWaiterRequestAttended: (requestId: string) => Promise<void>;
  setShowDetailModal: (show: boolean) => void;
  setHideTabBar: (hide: boolean) => void;
  setHideTabBarOnScroll: (hide: boolean) => void;
  setShowCartModal: (show: boolean) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export { AppContext };

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const auth = useAuth();
  const { selectedRestaurantId } = useSelectedRestaurant();
  
  // Cache para evitar peticiones duplicadas
  const categoriesCache = useRef<Map<string, { data: Category[], timestamp: number }>>(new Map());
  const menuCache = useRef<Map<string, { data: MenuItem[], timestamp: number }>>(new Map());
  const loadingPromises = useRef<Map<string, Promise<any>>>(new Map());
  
  // Tiempo de expiración del cache (5 minutos)
  const CACHE_EXPIRY = 5 * 60 * 1000;

  const loadCategories = useCallback(async (restaurantId?: string) => {
    const targetRestaurantId = restaurantId || selectedRestaurantId;
    if (!targetRestaurantId) {
      console.warn('⚠️ No hay restaurante seleccionado para cargar categorías');
      return;
    }

    // Verificar cache
    const cacheKey = `categories-${targetRestaurantId}`;
    const cached = categoriesCache.current.get(cacheKey);
    const now = Date.now();
    
    if (cached && (now - cached.timestamp) < CACHE_EXPIRY) {
      dispatch({ type: 'SET_CATEGORIES', payload: cached.data });
      return;
    }

    // Verificar si ya hay una petición en progreso
    if (loadingPromises.current.has(cacheKey)) {
      try {
        await loadingPromises.current.get(cacheKey);
        return;
      } catch (error) {
        console.error('❌ Error en petición de categorías en progreso:', error);
      }
    }

    // Iniciar nueva petición
    const promise = (async () => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        dispatch({ type: 'SET_ERROR', payload: null });
        
        let categories: Category[];
        
        if (auth.user?.uid) {
          // Usuario autenticado - usar adminMenuService
          categories = await adminMenuService.getCategoriesByRestaurant(auth.user.uid, targetRestaurantId) as Category[];
        } else {
          // Usuario no autenticado - usar API pública
          categories = await api.getCategories(targetRestaurantId);
        }
        
        // Guardar en cache
        categoriesCache.current.set(cacheKey, { data: categories, timestamp: now });
        
        dispatch({ type: 'SET_CATEGORIES', payload: categories });
      } catch (error) {
        console.error('❌ Error al cargar categorías:', error);
        dispatch({ type: 'SET_ERROR', payload: 'No se pudieron cargar las categorías. Verifica tu conexión.' });
        dispatch({ type: 'SET_CATEGORIES', payload: [] });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
        loadingPromises.current.delete(cacheKey);
      }
    })();

    loadingPromises.current.set(cacheKey, promise);
    await promise;
  }, [selectedRestaurantId, auth.user?.uid]);

  const loadMenu = useCallback(async (restaurantId?: string) => {
    const targetRestaurantId = restaurantId || selectedRestaurantId;
    if (!targetRestaurantId) {
      console.warn('⚠️ No hay restaurante seleccionado para cargar menú');
      return;
    }

    // Verificar cache
    const cacheKey = `menu-${targetRestaurantId}`;
    const cached = menuCache.current.get(cacheKey);
    const now = Date.now();
    
    if (cached && (now - cached.timestamp) < CACHE_EXPIRY) {
      dispatch({ type: 'SET_MENU_ITEMS', payload: cached.data });
      return;
    }

    // Verificar si ya hay una petición en progreso
    if (loadingPromises.current.has(cacheKey)) {
      try {
        await loadingPromises.current.get(cacheKey);
        return;
      } catch (error) {
        console.error('❌ Error en petición de menú en progreso:', error);
      }
    }

    // Iniciar nueva petición
    const promise = (async () => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        dispatch({ type: 'SET_ERROR', payload: null });
        
        let menuItems: MenuItem[];
        
        if (auth.user?.uid) {
          // Usuario autenticado - usar adminMenuService
          menuItems = await adminMenuService.getMenuItemsByRestaurant(auth.user.uid, targetRestaurantId) as MenuItem[];
        } else {
          // Usuario no autenticado - usar API pública
          menuItems = await api.getMenu(targetRestaurantId);
        }
        
        // Guardar en cache
        menuCache.current.set(cacheKey, { data: menuItems, timestamp: now });
        
        dispatch({ type: 'SET_MENU_ITEMS', payload: menuItems });
      } catch (error) {
        console.error('❌ Error al cargar menú:', error);
        dispatch({ type: 'SET_ERROR', payload: 'No se pudo cargar el menú. Verifica tu conexión.' });
        dispatch({ type: 'SET_MENU_ITEMS', payload: [] });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
        loadingPromises.current.delete(cacheKey);
      }
    })();

    loadingPromises.current.set(cacheKey, promise);
    await promise;
  }, [selectedRestaurantId, auth.user?.uid]);

  const loadMenuByCategory = useCallback(async (categoryId: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      
      if (!auth.user?.uid || !selectedRestaurantId) {
        console.warn('⚠️ No hay usuario autenticado o restaurante seleccionado');
        dispatch({ type: 'SET_MENU_ITEMS', payload: [] });
        return;
      }

      const menuItems = await adminMenuService.getMenuItemsByRestaurant(auth.user.uid, selectedRestaurantId) as MenuItem[];
      const filteredItems = menuItems.filter((item: MenuItem) => item.categoryId === categoryId);
      dispatch({ type: 'SET_MENU_ITEMS', payload: filteredItems });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'No se pudo cargar el menú de esta categoría.' });
      dispatch({ type: 'SET_MENU_ITEMS', payload: [] });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch, selectedRestaurantId, auth.user?.uid]);

  const addToCart = useCallback((menuItem: MenuItem, quantity: number, specialInstructions?: string, sizeName?: string) => {
    const orderItem: OrderItem = {
      id: `cart-${Date.now()}`,
      menuItem,
      quantity,
      specialInstructions,
      price: menuItem.price * quantity,
      size: sizeName,
    };
    dispatch({ type: 'ADD_TO_CART', payload: orderItem });
  }, [dispatch]);

  const removeFromCart = useCallback((itemId: string) => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: itemId });
  }, [dispatch]);

  const updateCartItem = useCallback((itemId: string, quantity: number) => {
    dispatch({ type: 'UPDATE_CART_ITEM', payload: { itemId, quantity } });
  }, [dispatch]);

  const clearCart = useCallback(() => {
    dispatch({ type: 'CLEAR_CART' });
  }, [dispatch]);

  const clearAllAppData = useCallback(() => {
    // Limpiar cache
    categoriesCache.current.clear();
    menuCache.current.clear();
    loadingPromises.current.clear();
    
    dispatch({ type: 'CLEAR_ALL_DATA', payload: undefined });
  }, [dispatch]);

  // Función para invalidar cache específico
  const invalidateCache = useCallback((type: 'categories' | 'menu' | 'all') => {
    switch (type) {
      case 'categories':
        categoriesCache.current.clear();
        break;
      case 'menu':
        menuCache.current.clear();
        break;
      case 'all':
        categoriesCache.current.clear();
        menuCache.current.clear();
        loadingPromises.current.clear();
        break;
    }
  }, []);

  // Funciones placeholder para órdenes y solicitudes de mesero
  const placeOrder = useCallback(async (orderData: {
    tableSigned?: string;
    deliveryAddress?: string;
  }): Promise<Order> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
    
      const order = await api.createOrder({
        ...orderData,
        items: state.cart
      });
      
      dispatch({ type: 'ADD_ORDER', payload: order });
      dispatch({ type: 'CLEAR_CART' });
      return order;
    } catch (error) {
      console.error('❌ AppContext - Error creando orden:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Error al realizar pedido' });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch, state.cart]);

  const setShowDetailModal = useCallback((show: boolean) => {
    dispatch({ type: 'SET_SHOW_DETAIL_MODAL', payload: show });
  }, [dispatch]);

  const setHideTabBar = useCallback((hide: boolean) => {
    dispatch({ type: 'SET_HIDE_TAB_BAR', payload: hide });
  }, [dispatch]);

  const setHideTabBarOnScroll = useCallback((hide: boolean) => {
    dispatch({ type: 'SET_HIDE_TAB_BAR_ON_SCROLL', payload: hide });
  }, [dispatch]);

  const setShowCartModal = useCallback((show: boolean) => {
    dispatch({ type: 'SET_SHOW_CART_MODAL', payload: show });
  }, [dispatch]);

  // Funciones placeholder para resolver el error de TypeScript
  const loadOrders = useCallback(async () => {
    // TODO: Implementar carga de órdenes
  }, []);

  const updateOrderStatus = useCallback(async (orderId: string, status: Order['status']) => {
    console.log('updateOrderStatus', orderId, status);
  }, []);

  const requestWaiter = useCallback(async (requestType: WaiterRequest['requestType'], message?: string): Promise<WaiterRequest> => {
    console.log('requestWaiter', requestType, message);
    return {} as WaiterRequest;
  }, []);

  const loadWaiterRequests = useCallback(async () => {
    // TODO: Implementar carga de solicitudes de mesero
  }, []);

  const markWaiterRequestAttended = useCallback(async (requestId: string) => {
    console.log('markWaiterRequestAttended', requestId);
  }, []);

  // Recargar menú cuando cambie el restaurante seleccionado
  useEffect(() => {
    // Solo cargar automáticamente si NO estamos en una página de admin
    // En admin, las páginas específicas cargarán sus propios datos cuando sea necesario
    const isAdminPage = window.location.pathname.startsWith('/admin');
    
    if (selectedRestaurantId && !isAdminPage) {
      loadCategories();
      loadMenu();
    }
  }, [selectedRestaurantId]); // Removidas las dependencias loadCategories y loadMenu para evitar re-ejecuciones

  // 1) Hidratar carrito desde localStorage al montar
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = await storageService.getItem('app_cart');
        const backup = await storageService.getItem('app_cart_backup');
        const base = raw || backup;
        if (base) {
          const parsed: OrderItem[] = JSON.parse(base);
          if (!cancelled && Array.isArray(parsed) && parsed.length > 0) {
            dispatch({ type: 'SET_CART', payload: parsed });
          }
        }
        // No limpiar backup aquí aún; se limpiará al guardar o al restaurar tras login
      } catch (e) {
        // noop
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // 2) Persistir carrito en localStorage cuando cambie
  useEffect(() => {
    (async () => {
      try {
        await storageService.setItem('app_cart', JSON.stringify(state.cart));
        // Si logramos persistir el carrito actual, podemos limpiar el backup viejo
        if (state.cart && state.cart.length > 0) {
          await storageService.removeItem('app_cart_backup' as any);
          await storageService.removeItem('app_cart_ctx' as any);
        }
      } catch (e) {
        // noop
      }
    })();
  }, [state.cart]);

  // 3) Tras login, restaurar backup si el carrito está vacío
  useEffect(() => {
    if (!auth.user?.uid) return;
    (async () => {
      try {
        const rawBackup = await storageService.getItem('app_cart_backup');
        if (rawBackup && (!state.cart || state.cart.length === 0)) {
          const parsed: OrderItem[] = JSON.parse(rawBackup);
          if (Array.isArray(parsed) && parsed.length > 0) {
            dispatch({ type: 'SET_CART', payload: parsed });
            await storageService.removeItem('app_cart_backup' as any);
            await storageService.removeItem('app_cart_ctx' as any);
            await storageService.setItem('app_cart', JSON.stringify(parsed));
          }
        }
      } catch (e) {
        // noop
      }
    })();
  }, [auth.user?.uid, state.cart?.length]);

  const value: AppContextType = {
    ...state,
    auth: {
      user: auth.user,
      isAuthenticated: auth.isAuthenticated,
      isLoading: auth.isLoading
    },
    dispatch,
    loadCategories,
    loadMenu,
    loadMenuByCategory,
    addToCart,
    removeFromCart,
    updateCartItem,
    clearCart,
    clearAllAppData,
    invalidateCache,
    placeOrder,
    setShowDetailModal,
    setHideTabBar,
    setHideTabBarOnScroll,
    setShowCartModal,
    loadOrders,
    updateOrderStatus,
    requestWaiter,
    loadWaiterRequests,
    markWaiterRequestAttended
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp debe usarse dentro de AppProvider');
  return ctx;
}; 